import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import useFormatGraphData from '@/hooks/charts/useFormatGraphData';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';

//hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
// Components
import TooltipCustom from './components/TooltipCustom';

import ChartWrapper from './ChartWrapper';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomAxisTick = ({ x, y, payload, format, isExport }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={10}
                textAnchor="end"
                transform="rotate(-45)"
                className={clsx('fill-current font-GSemiBold text-xxs text-icoGray-500', {
                    'dark:text-icoDarkMode-wolf': isExport
                })}>
                {format ? format(payload.value) : payload.value}
            </text>
        </g>
    );
};

CustomAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.object,
    format: PropTypes.func,
    isExport: PropTypes.bool
};

function fillEstimatedData(data) {
    let isEstimated = false;
    let dataFormatted = [...data]; // Create a shallow copy to avoid mutating the original

    for (let i = 0; i < dataFormatted.length; i++) {
        const currentEntry = dataFormatted[i];
        const previousEntry = dataFormatted[i - 1];
        const nextEntry = dataFormatted[i + 1];

        // Check if current entry is estimated
        if (currentEntry?.estimated) {
            isEstimated = true;
        }

        if (currentEntry?.value) {
            if (
                (!previousEntry?.value && previousEntry?.estimated) ||
                (!nextEntry?.value && nextEntry?.estimated)
            ) {
                // Create a new object instead of mutating the existing one
                dataFormatted[i] = {
                    ...currentEntry,
                    estimated: currentEntry.value
                };
            }
        }
    }

    return { dataFormatted, isEstimated };
}

const LineChartComponent = ({
    data,
    columnNames,
    activeTable,
    userNumbersFormat,
    isExport,
    hiddenKeys,
    height = 230
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const isDateChart = useMemo(
        () => Object.keys(data.name)[0] === 'date' || data?.isXAxisDate,
        [data]
    );
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTimeNoTz } = useFormatDateTime(userNumbersFormat?.date?.timezone);
    const { formatNullValue, formatNoDataArea } = useFormatGraphData();

    // Désactiver les animations si trop de données (évite le clignotement)
    const shouldAnimate = useMemo(() => {
        if (isExport) return false;
        return data?.graphData?.length < 50; // Pas d'animation si > 50 points
    }, [data?.graphData?.length, isExport]);

    const { dataFormatted, isEstimated } = fillEstimatedData(data.graphData);

    const areaNoData = useMemo(
        () => formatNoDataArea(dataFormatted),
        [dataFormatted, formatNoDataArea]
    );
    const isAverage = dataFormatted[0].avg || dataFormatted[0].avg === 0;
    const isEvolution =
        dataFormatted[0].evolution === null ||
        dataFormatted[0].evolution ||
        dataFormatted[0].evolution === 0;

    const userNumbersFormatMerged = {
        ...userNumbersFormat,
        date: {
            ...userNumbersFormat?.date,
            format: `${userNumbersFormat?.date?.format || ''}${data?.customDateFormatPrefix || ''}`
        }
    };
    const firstKey = Object.keys(dataFormatted[0]).find((value) => 'date' !== value);

    // Determine if we should render single area or multiple areas
    const shouldRenderSingleArea = useMemo(() => {
        return (
            Object.entries(dataFormatted[0])?.length < 3 ||
            (Object.entries(dataFormatted[0])?.length === 3 && isAverage) ||
            isEvolution ||
            Boolean('estimated' in (data.graphData?.[0] ?? {})) ||
            dataFormatted[0].organic >= 0
        );
    }, [dataFormatted, isAverage, isEvolution, data.graphData]);

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={dataFormatted}
                    valueFormat={data.valueFormat.notation !== 'compact' ? data.valueFormat : {}}
                    columnNames={columnNames}
                    dataName={data.name}
                    isEstimatedChart={isEstimated}
                    userNumbersFormat={userNumbersFormatMerged}
                />
            ) : (
                <SizeMe>
                    {({ size }) => (
                        <div
                            className="relative w-full lg:h-full"
                            style={{ minHeight: isExport ? 180 : height }}>
                            <div className="absolute left-0 top-0 h-full w-full">
                                <ResponsiveContainer
                                    height={isExport ? 180 : height}
                                    width={isExport ? data.fixedSize : '100%'}>
                                    <AreaChart
                                        data={
                                            isEstimated
                                                ? dataFormatted
                                                : formatNullValue(dataFormatted)
                                        }
                                        margin={{
                                            bottom:
                                                dataFormatted.length > 8 || size.width < 350
                                                    ? 25
                                                    : 0,
                                            top: 12, // Prevents cut off lines / areas that are too close to the top
                                            ...data.margin
                                        }}>
                                        <defs>
                                            <linearGradient
                                                id={`gradient-${data.id}`}
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1">
                                                <stop
                                                    offset="5%"
                                                    stopColor={
                                                        darkModeColorsChart[theme][
                                                            data.color['value']
                                                        ]
                                                    }
                                                    stopOpacity={0.3}
                                                />
                                                <stop
                                                    offset="95%"
                                                    stopColor={
                                                        darkModeColorsChart[theme][
                                                            data?.color['value']
                                                        ]
                                                    }
                                                    stopOpacity={0}
                                                />
                                            </linearGradient>
                                        </defs>
                                        {isAverage && !hiddenKeys?.['avg'] && (
                                            <ReferenceLine
                                                y={dataFormatted[0].avg}
                                                stroke={darkModeColorsChart[theme].referenceLine}
                                                strokeWidth={2}
                                            />
                                        )}
                                        <YAxis
                                            tickCount={5}
                                            domain={['auto', 'auto']}
                                            tickLine={false}
                                            minTickGap={-20}
                                            axisLine={false}
                                            tickFormatter={(item) =>
                                                formatNumber(item, data.valueFormat)
                                            }
                                        />
                                        <XAxis
                                            tick={
                                                dataFormatted.length > 8 || size.width < 350 ? (
                                                    <CustomAxisTick
                                                        format={(item) =>
                                                            formatDateTimeNoTz(
                                                                item,
                                                                userNumbersFormatMerged.date.format
                                                            )
                                                        }
                                                        isExport={isExport}
                                                    />
                                                ) : (
                                                    {}
                                                )
                                            }
                                            tickFormatter={
                                                dataFormatted.length <= 8 && size.width >= 350
                                                    ? (item) =>
                                                          formatDateTimeNoTz(
                                                              item,
                                                              userNumbersFormatMerged.date.format
                                                          )
                                                    : null
                                            }
                                            interval={
                                                // responsive xAxis labels
                                                isDateChart &&
                                                dataFormatted.length * 20 > size.width
                                                    ? Math.round(
                                                          dataFormatted.length / (size.width / 20)
                                                      )
                                                    : 0
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            minTickGap={-55}
                                            dataKey="date"
                                            type="category"
                                        />
                                        <CartesianGrid
                                            vertical={false}
                                            stroke={darkModeColorsChart[theme].grid}
                                        />
                                        {shouldRenderSingleArea && !hiddenKeys?.[firstKey] && (
                                            <>
                                                <Area
                                                    name={
                                                        data.name[firstKey]?.raw ||
                                                        t(data.name[firstKey])
                                                    }
                                                    type="monotone"
                                                    dataKey={firstKey}
                                                    isAnimationActive={shouldAnimate}
                                                    animationDuration={300}
                                                    stroke={
                                                        darkModeColorsChart[theme][
                                                            data.color[firstKey]
                                                        ]
                                                    }
                                                    strokeWidth={2.5}
                                                    fill={`url(#gradient-${data.id})`}
                                                    activeDot={{
                                                        stroke: darkModeColorsChart[theme].dot,
                                                        strokeWidth: 4.5,
                                                        r: 5
                                                    }}
                                                />
                                                {isEstimated && (
                                                    <Area
                                                        name={
                                                            data.name[firstKey]?.raw ||
                                                            t(data.name[firstKey])
                                                        }
                                                        type="monotone"
                                                        dataKey="estimated"
                                                        isAnimationActive={shouldAnimate}
                                                        animationDuration={300}
                                                        stroke={
                                                            darkModeColorsChart[theme][
                                                                data.color[firstKey]
                                                            ]
                                                        }
                                                        strokeLinecap="round"
                                                        strokeDasharray="2 8"
                                                        strokeWidth={2.5}
                                                        fillOpacity={0}
                                                        activeDot={{
                                                            stroke: darkModeColorsChart[theme].dot,
                                                            strokeWidth: 4.5,
                                                            r: 5
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )}
                                        {!shouldRenderSingleArea &&
                                            Object.entries(dataFormatted[0]).map((item, index) => {
                                                if (item[0] !== 'date' && !hiddenKeys?.[item[0]]) {
                                                    return (
                                                        <Area
                                                            key={index}
                                                            name={
                                                                data.name[item[0]]?.raw ||
                                                                t(data.name[item[0]])
                                                            }
                                                            type="monotone"
                                                            dataKey={item[0]}
                                                            isAnimationActive={shouldAnimate}
                                                            animationDuration={300}
                                                            stroke={
                                                                darkModeColorsChart[theme][
                                                                    data.color[item[0]]
                                                                ]
                                                            }
                                                            strokeWidth={2.5}
                                                            fillOpacity={0}
                                                            activeDot={{
                                                                stroke: darkModeColorsChart[theme]
                                                                    .dot,
                                                                strokeWidth: 4.5,
                                                                r: 5
                                                            }}
                                                            connectNulls
                                                        />
                                                    );
                                                }
                                            })}
                                        <Tooltip
                                            cursor={false}
                                            content={
                                                <TooltipCustom
                                                    tooltipTranslation={data.tooltipTranslation}
                                                    valueFormat={
                                                        data.valueFormat.notation !== 'compact'
                                                            ? data.valueFormat
                                                            : {}
                                                    }
                                                    data={data}
                                                    userNumbersFormat={userNumbersFormatMerged}
                                                />
                                            }
                                        />
                                        {isEstimated ? (
                                            <ReferenceArea
                                                x1={areaNoData.x1}
                                                x2={areaNoData.x2}
                                                fill={darkModeColorsChart[theme].gray}
                                                fillOpacity={0.3}
                                            />
                                        ) : null}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </SizeMe>
            )}
        </>
    );
};

LineChartComponent.propTypes = {
    data: PropTypes.object,
    columnNames: PropTypes.object,
    activeTable: PropTypes.bool,
    userNumbersFormat: PropTypes.object,
    height: PropTypes.number,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const LineChart = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        {...restProps}>
        <LineChartComponent />
    </ChartWrapper>
);

LineChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { LineChart, LineChartComponent };
